/**
 * What a batch hands back: every pair's artifacts under its own folder, plus one
 * summary that accounts for the whole run.
 *
 * The summary exists because a zip of thirty files is not a report. Someone
 * opening this needs to know, without unpacking anything, which sources were
 * analysed, what each one's verdict was, what the export gate withheld, and —
 * most importantly — which files were NOT analysed and why. A batch that quietly
 * dropped half a folder would be worse than one that refused it outright.
 */

import { slugify } from "../../engine/export";
import type { BatchArtifact } from "../../workers/batch-protocol";
import type { BatchResult } from "./run-batch";

export function batchZipFileName(generatedAt: string): string {
  const stamp = generatedAt.replace(/[:.]/g, "-").replace(/Z$/, "");
  return `batch-${stamp}.zip`;
}

/** Artifacts for the archive: one folder per source, summary at the root. */
export function buildBatchArchiveEntries(result: BatchResult, generatedAt: string): BatchArtifact[] {
  const entries: BatchArtifact[] = [{ fileName: "batch-summary.md", content: buildBatchSummary(result, generatedAt) }];
  for (const outcome of result.outcomes) {
    if (outcome.status !== "analysed") continue;
    for (const artifact of outcome.artifacts) {
      entries.push({ fileName: `${slugify(outcome.profileId)}/${artifact.fileName}`, content: artifact.content });
    }
  }
  return entries;
}

export function buildBatchSummary(result: BatchResult, generatedAt: string): string {
  const analysed = result.outcomes.filter((outcome) => outcome.status === "analysed");
  const lines: string[] = [
    "# Batch analysis summary",
    "",
    `Generated: ${generatedAt}`,
    `Pairs analysed: ${analysed.length} of ${result.plan.pairs.length} planned`,
    "",
    "Every pair here was analysed with no human decisions recorded — this is the",
    "state of each run before anyone reviewed it. Open a source in the app to",
    "decide anything.",
    ""
  ];

  if (result.outcomes.length > 0) {
    lines.push("## Runs", "", "| Source | Reference | Candidate | Verdict | Findings |", "|---|---|---|---|---|");
    for (const outcome of result.outcomes) {
      const verdict =
        outcome.status === "analysed"
          ? outcome.verdict
          : outcome.status === "refused"
            ? "Refused"
            : "Failed";
      const findings = outcome.status === "analysed" ? String(outcome.findingCount) : "—";
      lines.push(
        `| ${outcome.profileId} | ${outcome.referenceFileName} | ${outcome.candidateFileName} | ${verdict} | ${findings} |`
      );
    }
    lines.push("");
  }

  const withFailures = analysed.filter((outcome) => outcome.status === "analysed" && outcome.failures.length > 0);
  if (withFailures.length > 0) {
    lines.push("## Why a run did not come back clean", "");
    for (const outcome of withFailures) {
      if (outcome.status !== "analysed") continue;
      lines.push(`- **${outcome.profileId}**`);
      for (const failure of outcome.failures) lines.push(`  - ${failure}`);
    }
    lines.push("");
  }

  const withBlocked = analysed.filter((outcome) => outcome.status === "analysed" && outcome.blocked.length > 0);
  if (withBlocked.length > 0) {
    lines.push("## Artifacts the export gate withheld", "");
    for (const outcome of withBlocked) {
      if (outcome.status !== "analysed") continue;
      for (const blocked of outcome.blocked) {
        lines.push(`- **${outcome.profileId}** — \`${blocked.kind}\`: ${blocked.reason}`);
      }
    }
    lines.push("");
  }

  const notAnalysed = result.outcomes.filter((outcome) => outcome.status !== "analysed");
  if (notAnalysed.length > 0 || result.plan.skipped.length > 0) {
    lines.push("## Not analysed", "");
    for (const outcome of notAnalysed) {
      lines.push(`- **${outcome.profileId}** (${outcome.referenceFileName}, ${outcome.candidateFileName}) — ${outcome.reason}`);
    }
    for (const group of result.plan.skipped) {
      lines.push(`- **${group.profileId ?? "unidentified"}** (${group.fileNames.join(", ")}) — ${group.reason}`);
    }
    lines.push("");
  }

  // The count a reader should check against the folder they picked.
  const accountedFor =
    result.outcomes.length * 2 + result.plan.skipped.reduce((total, group) => total + group.fileNames.length, 0);
  lines.push(`Files accounted for: ${accountedFor}.`, "");
  return lines.join("\n");
}
